"use client";

/**
 * Truemee ランディングページ - DashboardClient
 *
 * レスポンシブ対応:
 * - PC版 (768px以上): 画面幅に合わせて柔軟にスケーリング（100%幅）
 * - SP版 (768px未満): 750px基準でスケーリング
 *
 * FV設定はAPIから動的に取得し、画像やテキストを反映
 * LP variantに応じてAまたはBを表示
 */

import React, { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import TruemeePcA from "./TruemeePcA";
import TruemeeSpA from "./TruemeeSpA";
import DashboardMobileMenu from "./components/DashboardMobileMenu";
import LpVersionBContent from "./components/LpVersionBContent";

// FV設定の型定義
interface FvConfig {
  activeId: string;
  elements?: Array<{
    id: string;
    type: string;
    src?: string;
    content?: string;
  }>;
  variants: Array<{
    id: string;
    label: string;
    images: {
      hero: string;
      heroMask?: string;
      [key: string]: string | undefined;
    };
  }>;
}

// API URL
const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://truemee.jp/api';

const BREAKPOINT = 768;

// PC版デザイン設定（基準サイズ）
const PC_DESIGN = {
  baseWidth: 1920,  // デザインの基準幅
  height: 14000,    // 余白を圧縮
  bgColor: "#fcfcfc",
};

// SP版デザイン設定
const SP_DESIGN = {
  width: 750,
  height: 20100, // 余白圧縮後の高さ
  bgColor: "#fffaf8",
};

export default function DashboardClient() {
  const searchParams = useSearchParams();
  const [isMobile, setIsMobile] = useState(false);
  const [scale, setScale] = useState(1);
  const [mounted, setMounted] = useState(false);
  const [fvConfig, setFvConfig] = useState<FvConfig | null>(null);
  const [lpVariant, setLpVariant] = useState<string>("a");

  // URLパラメータでプレビューモードをチェック
  const previewVariant = searchParams.get("preview");

  // FV設定をAPIから取得
  useEffect(() => {
    const fetchFvConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/v2/public/fv-config/`);
        if (response.ok) {
          const data = await response.json();
          setFvConfig(data);
        }
      } catch (error) {
        console.error('FV設定の取得に失敗:', error);
      }
    };
    fetchFvConfig();
  }, []);

  // LP variantをAPIから取得（プレビューモードでない場合のみ）
  useEffect(() => {
    // プレビューモードの場合はAPI取得しない
    if (previewVariant) {
      setLpVariant(previewVariant);
      return;
    }

    const fetchLpVariant = async () => {
      try {
        console.log('🔄 Fetching LP variant from:', `${API_URL}/v2/public/lp-variant/`);
        const response = await fetch(`${API_URL}/v2/public/lp-variant/`);
        if (response.ok) {
          const data = await response.json();
          console.log('✅ LP variant response:', data);
          if (data.variant_id) {
            console.log('🔀 Setting LP variant to:', data.variant_id);
            setLpVariant(data.variant_id);
          }
        }
      } catch (error) {
        console.error('LP variant取得に失敗:', error);
      }
    };
    fetchLpVariant();
  }, [previewVariant]);

  useEffect(() => {
    setMounted(true);

    const updateLayout = () => {
      const viewportWidth = window.innerWidth;
      const mobile = viewportWidth < BREAKPOINT;
      setIsMobile(mobile);

      if (mobile) {
        // SP版: 750px基準でスケーリング
        const newScale = Math.min(1, viewportWidth / SP_DESIGN.width);
        setScale(newScale);
      } else {
        // PC版: 画面幅に合わせて柔軟にスケーリング
        // 常に画面幅100%を使用（最大スケールは1920px基準で1.0）
        const newScale = viewportWidth / PC_DESIGN.baseWidth;
        setScale(newScale);
      }
    };

    updateLayout();
    window.addEventListener("resize", updateLayout);
    return () => window.removeEventListener("resize", updateLayout);
  }, []);

  // SSR対応: マウント前はローディング表示
  if (!mounted) {
    return (
      <div className="w-full min-h-screen bg-[#fcfcfc] flex items-center justify-center">
        <div className="text-[#ed7243] text-2xl">Truemee</div>
      </div>
    );
  }

  // LP Version B の場合
  if (lpVariant === "b") {
    return (
      <div className="min-h-screen bg-white font-sans">
        <LpVersionBContent />
      </div>
    );
  }

  // LP Version A (デフォルト)
  if (isMobile) {
    // SP版のレンダリング
    return (
      <div
        className="w-full min-h-screen overflow-x-hidden"
        style={{ backgroundColor: SP_DESIGN.bgColor }}
      >
        <DashboardMobileMenu />
        <div className="flex justify-center">
          <div
            className="transition-all duration-300 ease-out"
            style={{
              width: `${SP_DESIGN.width * scale}px`,
              height: `${SP_DESIGN.height * scale}px`,
            }}
          >
            <div
              className="relative origin-top-left will-change-transform overflow-hidden"
              style={{
                width: `${SP_DESIGN.width}px`,
                height: `${SP_DESIGN.height}px`,
                transform: `scale(${scale})`,
              }}
            >
              <TruemeeSpA />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // PC版のレンダリング（画面幅100%で柔軟に対応）
  return (
    <div
      className="w-full min-h-screen overflow-x-hidden"
      style={{ backgroundColor: PC_DESIGN.bgColor }}
    >
      <div
        className="transition-all duration-300 ease-out"
        style={{
          width: "100%",
          height: `${PC_DESIGN.height * scale}px`,
        }}
      >
        <div
          className="relative origin-top-left will-change-transform overflow-hidden"
          style={{
            width: `${PC_DESIGN.baseWidth}px`,
            height: `${PC_DESIGN.height}px`,
            transform: `scale(${scale})`,
          }}
        >
          <TruemeePcA fvConfig={fvConfig} />
        </div>
      </div>
    </div>
  );
}
